import React, { useMemo } from 'react'
import { Box, Button, CircularProgress, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import Text from '../../../../atoms/Text'
import { useFormattingContext } from '../../../../organisms/FormattingProvider/FormattingContext'
import OperationalTable from '../../../../organisms/OperationalTable'
import DebouncedInput from '../../../../molecules/DebouncedInput'
import Avatar from '../../../../atoms/Avatar'
import Icon from '../../../../atoms/Icon'
import { useAccountClientsActions } from './AccountClientsTableContext'
const useStyles = makeStyles((theme) => ({
  clientAccountsTable: {
    marginTop: '10px',

    '& .__action-button': {
      textTransform: 'none',
      borderRadius: 'var(--border-radius-lg)'
    },
    '& .__removed': {
      color: 'var(--color-danger-500)'
    },
    '& .__added': {
      color: 'var(--color-success-500)'
    },
    '& .__acct-cell': {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: '36px'
    }
  },
  clientTableHeader: {
    paddingBottom: '10px',
    display: 'flex',
    flexDirection: 'row'
  },
  search: {
    maxWidth: '300px',
    maxHeight: '35px'
  },
  selectedItemContainer: {
    display: 'flex',
    alignItems: 'center',
    border: '2px solid #EEF0F8',
    borderRadius: 'var(--border-radius-md)',
    padding: '0.75rem 1rem'
  },
  itemContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem'
  },
  clientInfoContainer: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem'
  },
  clientTableErrorSmall: {
    textAlign: 'center',
    color: 'dimgray'
  },
  clientTableError: {
    textAlign: 'center',
    color: 'var(--color-danger-500)'
  },
  spinningLoader: {
    paddingLeft: '1rem'
  }
}))
const ClientCell = ({ name, avatarSrc, id }) => {
  const classes = useStyles()
  const { onClickClient } = useAccountClientsActions()
  return (
    <div
      className={classes.clientInfoContainer}
      onClick={() => onClickClient(id)}
    >
      <Avatar
        customSize='2.5rem'
        src={avatarSrc}
        avatarLetters={name}
      />
      <Box ml='2rem'>
        <Text text={name} customFontWeight='600' />
      </Box>
    </div>
  )
}

ClientCell.propTypes = {
  name: PropTypes.string,
  avatarSrc: PropTypes.string,
  id: PropTypes.number
}

const EditActionCell = ({ row }) => {
  const { updateAssignment } = useAccountClientsActions()

  if (row.assigned && !row.edited) {
    return (
      <Button
        className='__action-button __removed'
        variant='outlined'
        startIcon={<Icon name='checkCircle' />}
        onClick={() => {
          updateAssignment(row.value.id)
        }}
      >
        Remove
      </Button>
    )
  }
  if (row.assigned && row.edited) {
    return (
      <Button
        className='__action-button __added'
        variant='outlined'
        startIcon={<Icon name='checkCircle' />}
        onClick={() => {
          updateAssignment(row.value.id)
        }}
      >
        Add Back
      </Button>
    )
  }
  if (!row.assigned && !row.edited) {
    return (
      <Button
        className='__action-button __added'
        variant='outlined'
        startIcon={<Icon name='checkCircle' />}
        onClick={() => {
          updateAssignment(row.value.id)
        }}
      >
        Add
      </Button>
    )
  }
  return (
    <div>
      <Button
        className='__action-button __removed'
        variant='outlined'
        startIcon={<Icon name='checkCircle' />}
        onClick={() => {
          updateAssignment(row.value.id)
        }}
      >
        Undo
      </Button>
    </div>
  )
}

EditActionCell.propTypes = {
  row: PropTypes.object
}

const defaultColumnConfig = {
  columns: [
    {
      id: 'clientName',
      Header: 'Household',
      accessor: 'value.name',
      Cell: ({ row }) => ClientCell({ name: row.original.value.name, avatarSrc: row.original.value.imageUrl, id: row.original.value.id })
    },
    {
      id: 'clientNumber',
      accessor: 'value.id',
      Header: 'Household ID'
    }
  ],
  defaultSort: [{ id: 'clientNumber', desc: false }],
  editingSort: [{ id: 'actions', desc: false }]
}

const editingColumns = [{
  id: 'actions',
  accessor: 'assigned',
  Header: 'Actions',
  width: 200,
  Cell: ({ row }) => EditActionCell({ row: row.original })
}]
export const useColumns = (editing) => {
  const { formatter } = useFormattingContext()
  return useMemo(() => {
    const { columns, defaultSort, editingSort } = defaultColumnConfig
    const allColumns = editing ? [...columns, ...editingColumns] : columns
    const sorting = editing ? defaultSort : editingSort
    const templatedColumns = allColumns.map((column) => {
      if (column?.Cell) return column

      return {
        ...column,
        Cell: ({ value }) => formatter(value, column.format)
      }
    })

    return {
      columns: templatedColumns,
      sorting
    }
  }, [formatter, editing])
}

const AccountClientsTable = ({ editing, isLoading }) => {
  const classes = useStyles()
  const { searchEditList, filteredList, saving } = useAccountClientsActions()
  const assignedClientCount = filteredList.filter((client) => {
    return client.assigned
  }).length
  const data = editing ? filteredList : filteredList.filter((client) => {
    return client.assigned
  })
  const columnConfig = useColumns(editing)

  return (
    <OperationalTable.Wrapper className={classes.clientAccountsTable}>
      <div className={classes.clientTableHeader}>
        <DebouncedInput
          className={classes.search}
          placeholder='Search Households'
          onChange={searchEditList}
        />
        {saving &&
          <div className={classes.spinningLoader}>
            <CircularProgress />
          </div>}
      </div>
      {
        !editing && assignedClientCount === 0 ? (
          <div className={classes.clientTableErrorSmall}>
            No household assigned. You can assign a household to this account via edit.
          </div>
        ) : filteredList.length > 0 ? (
          <OperationalTable
            variant='v2'
            loading={false}
            mode='auto'
            columns={columnConfig.columns || []}
            defaultSort={columnConfig.defaultSort || []}
            data={data}
            total={0}
            itemName='Clients'
            hideFooter
            hideHeader
          />
        ) : isLoading ? (
          <div className={classes.spinningLoader}>
            <CircularProgress />
          </div>
        ) : (
          <div className={classes.clientTableErrorSmall}>
            No results found. Try adjusting your search terms.
          </div>
        )
      }
    </OperationalTable.Wrapper>
  )
}

AccountClientsTable.propTypes = {
  editing: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default AccountClientsTable
